/**
 * Типизированные ошибки SDK.
 *
 * Каждая ошибка из родителя приходит как `{code, message}`. Класс SDKError
 * позволяет играм программно различать ситуации:
 *
 * ```ts
 * try {
 *   await IgrayTut.storage.save({ level: 5 });
 * } catch (e) {
 *   if (e instanceof IgrayTut.SDKError) {
 *     if (e.code === "quota_exceeded") showPremiumPrompt();
 *     else if (e.code === "rate_limit_exceeded") setTimeout(retry, e.retryAfterMs);
 *   }
 * }
 * ```
 *
 * Старый код (до Волны 7) получал голый `new Error(message)` без кода — это
 * оставлено в обратной совместимости через `SDK_ERROR_CODES.UNKNOWN`.
 */

export const SDK_ERROR_CODES = {
  /** Облачное сохранение превысило квоту (100 КБ free / 10 МБ Premium). HTTP 413. */
  QUOTA_EXCEEDED: "quota_exceeded",
  /** Слишком частые запросы — rate-limit сервера. HTTP 429. */
  RATE_LIMIT_EXCEEDED: "rate_limit_exceeded",
  /** Для гостей: сохранение требует логин. HTTP 401. */
  GUEST_NOT_SUPPORTED: "guest_not_supported",
  /** Пользователь не авторизован (более общий случай). HTTP 401. */
  AUTH_REQUIRED: "auth_required",
  /** Игра с таким slug не найдена. HTTP 404. */
  UNKNOWN_GAME: "unknown_game",
  /** Попытка обратиться к методу которого нет. */
  UNKNOWN_METHOD: "unknown_method",
  /** Неверный формат запроса (проблема Zod-валидации). HTTP 422. */
  VALIDATION_FAILED: "validation_failed",
  /** Внутренняя ошибка сервера. HTTP 500. */
  INTERNAL_ERROR: "internal_error",
  /** Не удалось установить соединение с сервером. */
  NETWORK_ERROR: "network_error",
  /** Запрос ушёл, но ответ не пришёл за таймаут (30 сек). */
  TIMEOUT: "timeout",
  /** Фича временно отключена через admin kill-switch. HTTP 503. */
  FEATURE_DISABLED: "feature_disabled",
  /** Неопознанная ошибка. */
  UNKNOWN: "unknown_error",
} as const;

export type SDKErrorCode = (typeof SDK_ERROR_CODES)[keyof typeof SDK_ERROR_CODES] | string;

export interface SDKErrorOptions {
  /** Машинно-читаемый код ошибки. См. `SDK_ERROR_CODES`. */
  code?: SDKErrorCode;
  /** HTTP-статус если ошибка пришла из fetch. */
  status?: number;
  /** Через сколько мс можно повторить запрос (для `rate_limit_exceeded`). */
  retryAfterMs?: number;
  /** Дополнительные детали от сервера. */
  details?: unknown;
}

/**
 * Ошибка SDK. Бросается транспортом при reject ответа от родителя.
 *
 * Всегда имеет `.code` (если неизвестен — `SDK_ERROR_CODES.UNKNOWN`).
 */
export class SDKError extends Error {
  public readonly code: SDKErrorCode;
  public readonly status?: number;
  public readonly retryAfterMs?: number;
  public readonly details?: unknown;

  constructor(message: string, options: SDKErrorOptions = {}) {
    super(message);
    this.name = "SDKError";
    this.code = options.code ?? SDK_ERROR_CODES.UNKNOWN;
    this.status = options.status;
    this.retryAfterMs = options.retryAfterMs;
    this.details = options.details;

    // Восстановить прототип (Babel/esbuild class extends quirk)
    Object.setPrototypeOf(this, SDKError.prototype);
  }
}

/**
 * Проверка кода ошибки с type-guard. Удобно в `catch`:
 *
 * ```ts
 * } catch (e) {
 *   if (isSDKErrorCode(e, "quota_exceeded")) { ... }
 * }
 * ```
 */
export function isSDKErrorCode(err: unknown, code: SDKErrorCode): err is SDKError {
  return err instanceof SDKError && err.code === code;
}
